import React, { useContext, useCallback, MouseEvent } from 'react'
import NextLink from 'next/link'
import { Grid, Typography, Button, styledComponent } from '@hermes/web-components'
import { SearchSpecialistsContext } from '../../../../providers/SearchSpecialists'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import PracticeSpecialistCard from './PracticeSpecialistCard'
import SpecialistsOverview from './SpecialistsOverview'
import { practiceSpecialistSort } from '../../../../utils/practiceSpecialist'
import { themedColor } from '../../styles'
import { useWpaTenant } from '../../../../hooks/useWpaTenant'
import { AppDataContext } from '../../../../providers/AppData'

const ColumnsRoot = styledComponent(Grid)(({ theme }) => ({
  columnCount: 2,
  columnWidth: '50%',
  columnGap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    columnCount: 1,
    columnGap: 0,
    columnWidth: '100%'
  }
}))

interface PracticeSpecialistsProps {
  slug: string
}

// todo: move out if it's grow
const useComponentsConfig = () => {
  const { isWPA } = useWpaTenant()

  return { renderLoadMoreAsLink: !isWPA }
}

function PracticeSpecialists({ slug }: PracticeSpecialistsProps) {
  const translate = useTranslateMessage()
  const { locale } = useContext(AppDataContext)
  const {
    state: {
      specialists,
      totalCount,
      filters: { keyword, more },
      canLoadNext
    },
    changeMore
  } = useContext(SearchSpecialistsContext)
  const { buttonBorderRadius, loadMoreButtonFocusStyle } = themedColor()
  const { renderLoadMoreAsLink } = useComponentsConfig()

  const page = more

  const onChangeMore = useCallback(
    (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      event.nativeEvent.preventDefault()
      event.preventDefault()
      changeMore(more + 1)
    },
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [more]
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <Typography variant="h2" component="h2" fontWeight="200">
          {translate('specialists')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <SpecialistsOverview countSpecialists={totalCount} />
      </Grid>
      <ColumnsRoot item>
        {practiceSpecialistSort(specialists || []).map((spec) => (
          <PracticeSpecialistCard
            key={`practice-specialist-${spec.slug}`}
            specialist={spec}
            selectedKeyword={keyword}
          />
        ))}
      </ColumnsRoot>
      {canLoadNext && (
        <Grid xs={12} display="flex" justifyContent="center" sx={{ mt: 2 }}>
          {renderLoadMoreAsLink ? (
            <NextLink
              prefetch={false}
              style={{ color: 'inherit', textDecoration: 'inherit' }}
              href={`/${locale}/practice/${slug}/team/page-${page ? page + 1 : 1}`}
            >
              <Button
                variant="outlined"
                sx={{
                  display: canLoadNext ? 'inline-flex' : 'none',
                  borderRadius: buttonBorderRadius,
                  '&:focus, &:active': loadMoreButtonFocusStyle
                }}
                onClick={onChangeMore}
              >
                {translate('load_more_specialists')}
              </Button>
            </NextLink>
          ) : (
            <Button
              variant="outlined"
              sx={{
                display: canLoadNext ? 'inline-flex' : 'none',
                borderRadius: buttonBorderRadius,
                '&:focus, &:active': loadMoreButtonFocusStyle
              }}
              onClick={onChangeMore}
            >
              {translate('load_more_specialists')}
            </Button>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default PracticeSpecialists
